import { HIGHLIGHTED_STATE, SELECTED_STATE } from 'store/filters/constants';
import {
  canAddNode,
  GetNodeSelectionStyles,
  getSize,
  IsObjectEmpty,
} from 'utils/graph-helper';
import {
  contextSpecificFilters,
  isEdgeIncluded,
  addNodeCtx,
  createFilterCtx,
} from 'utils/graph-item-helper';
import { perfMark, perfMeasure, perfCount } from 'utils/graph-perf';
import { getClusterableAffiliation as getClusterableAffiliationShared } from 'utils/affiliation';

// Per-link memoization for the two stripped variants (S5).
// Keyed on the underlying link reference. WeakMap GCs naturally as the
// graph evolves. Each entry has at most two values: a base-stripped item
// and an active-color-stripped item.
const _stripPlainCache = new WeakMap();
const _stripActiveColorCache = new WeakMap();

const stripLinkDataMemo = (link) => {
  const cached = _stripPlainCache.get(link);
  if (cached) return cached;
  const result = {};
  const keys = Object.keys(link);
  for (let i = 0; i < keys.length; i++) {
    const k = keys[i];
    if (k !== 'data') result[k] = link[k];
  }
  _stripPlainCache.set(link, result);
  return result;
};

const stripLinkDataWithActiveColorMemo = (link) => {
  const cached = _stripActiveColorCache.get(link);
  if (cached) return cached;
  const result = {};
  const keys = Object.keys(link);
  for (let i = 0; i < keys.length; i++) {
    const k = keys[i];
    if (k !== 'data') result[k] = link[k];
  }
  result.color = link.activeColor;
  _stripActiveColorCache.set(link, result);
  return result;
};

export const selectItemsHelper = (
  nodes,
  links,
  contextIds,
  filtersForEdges,
  edgeFilterType,
  nodeOptions,
  nodeFilterType,
  regions,
  nodeFilters,
  nodeTypeFilterOut,
  shortestPathNodeid = '',
  timeFilters,
  contexts,
  hiddenNodes,
  contextFilters,
  affiliationClusterMode = false,
) => {
  perfMark('selector:items:start');
  perfCount('selector:items:runs');
  const items = {};
  const nodesAlreadyStyled = {};
  // add context nodes
  const ids = Object.keys(contextIds);
  const p2pIds = [];

  // Get selected affiliation filters for clustering
  const selectedAffiliations = nodeFilters?.affiliation || {};
  const selectedAffiliationKeys = Object.keys(selectedAffiliations).map((key) =>
    key.toLowerCase(),
  );

  // Affiliation parsing is shared (audit S10 / H17) — see src/utils/affiliation.js.
  const getClusterableAffiliation = (nodeAffiliation) =>
    getClusterableAffiliationShared(nodeAffiliation, selectedAffiliationKeys, affiliationClusterMode);

  // Helper: Build minimal data object for clustering
  const buildNodeData = (clusterAffiliation) =>
    clusterAffiliation ? { affliationUnit: clusterAffiliation } : undefined;

  ids.forEach((x) => {
    if (x.includes('p2p')) {
      p2pIds.push(contexts[x]);
    } else if (!x.includes('nl-message-search')) {
      const nodeObj = nodes[x]?.node;
      if (nodeObj) {
        const { data, ...nodeToAdd } = nodeObj;
        // Preserve full data for filter_session hub (annotation needs filterCount, resultCount, filters)
        if (nodeObj.nodeType === 'filter_session') {
          items[x] = { ...nodeToAdd, data };
        } else {
          const clusterAffiliation = getClusterableAffiliation(data?.affliationUnit);
          const nodeData = buildNodeData(clusterAffiliation);
          items[x] = nodeData ? { ...nodeToAdd, data: nodeData } : nodeToAdd;
        }
        nodesAlreadyStyled[x] = x;
      }
    }
  });

  contextSpecificFilters(
    items,
    nodes,
    links,
    hiddenNodes,
    contexts,
    contextFilters,
  );

  const { edgeFilters, removeEdges } = filtersForEdges;
  const nodeFiltersEnabled =
    nodeOptions.length > 0 || nodeTypeFilterOut.length > 0;
  const isRegionsFilterEmpty = IsObjectEmpty(regions);
  const constitencies = Object.keys(nodeFilters.constituency);
  const nodeSizingType = nodeFilters.nodeSizingType;
  const isConstituenciesEmpty = constitencies.length < 1;

  // Pre-compute filter context once, passed to all addNodeCtx calls
  const filterCtx = createFilterCtx({
    nodeFiltersEnabled,
    nodeFilterType,
    nodeTypeFilterOut,
    nodeOptions,
    nodeSizingType,
    constitencies,
    isRegionsFilterEmpty,
    isConstituenciesEmpty,
    nodeFilters,
    hiddenNodes,
    affiliationClusterMode,
  });

  const hasRemoveEdges = removeEdges.length > 0;
  const hasEdgeFilters = edgeFilters.length > 0;

  // Use module-scoped memoized strippers (S5) — repeated selectItems runs
  // with the same link references return the same stripped objects.
  const stripLinkData = stripLinkDataMemo;
  const stripLinkDataWithActiveColor = stripLinkDataWithActiveColorMemo;

  // add nodes from links
  for (const linkId in links) {
    const graphLink = links[linkId]?.link;
    if (
      (hasRemoveEdges &&
        isEdgeIncluded(graphLink, removeEdges, timeFilters)) ||
      items[linkId] !== undefined
    ) {
      continue;
    }

    const [nodeOneId, nodeTwoId] = linkId.split('/p/');
    const nodeOne = nodes[nodeOneId]?.node;
    const nodeTwo = nodes[nodeTwoId]?.node;

    // Early termination: if either node doesn't exist, skip
    if (!nodeOne || !nodeTwo) continue;

    if (!hasEdgeFilters) {
      // No edge filters: show normal node graph
      const addedNodeOne = addNodeCtx(nodeOne, items, nodeOneId, filterCtx);
      const addedNodeTwo = addNodeCtx(nodeTwo, items, nodeTwoId, filterCtx);

      if (addedNodeOne && addedNodeTwo) {
        items[linkId] = stripLinkData(graphLink);
      }
    } else if (edgeFilterType === HIGHLIGHTED_STATE) {
      const isIncluded = isEdgeIncluded(graphLink, edgeFilters, timeFilters, true);
      if (isIncluded) {
        const addedNodeOne = addNodeCtx(nodeOne, items, nodeOneId, filterCtx, nodesAlreadyStyled, false, true);
        const addedNodeTwo = addNodeCtx(nodeTwo, items, nodeTwoId, filterCtx, nodesAlreadyStyled, false, true);

        if (addedNodeOne && addedNodeTwo) {
          items[linkId] = stripLinkDataWithActiveColor(graphLink);
        }
      } else {
        const addedNodeOne = addNodeCtx(nodeOne, items, nodeOneId, filterCtx);
        const addedNodeTwo = addNodeCtx(nodeTwo, items, nodeTwoId, filterCtx);

        if (addedNodeOne && addedNodeTwo) {
          items[linkId] = stripLinkData(graphLink);
        }
      }
    } else {
      // filterType is selected state
      const isIncluded = isEdgeIncluded(graphLink, edgeFilters, timeFilters);
      if (isIncluded) {
        const addedNodeOne = addNodeCtx(nodeOne, items, nodeOneId, filterCtx, {}, true);
        const addedNodeTwo = addNodeCtx(nodeTwo, items, nodeTwoId, filterCtx, {}, true);

        if (addedNodeOne && addedNodeTwo) {
          items[linkId] = stripLinkDataWithActiveColor(graphLink);
        }
      }
    }
  }

  // Helper: build node item without data, with size and optional cluster data.
  // Used by "left over nodes" and "p2p" sections to avoid repeated destructuring/spread.
  const buildLeftoverNodeItem = (node) => {
    const result = {};
    const keys = Object.keys(node);
    for (let i = 0; i < keys.length; i++) {
      const k = keys[i];
      if (k !== 'data') result[k] = node[k];
    }
    result.size = getSize(nodeSizingType, node.data);
    const clusterAffiliation = getClusterableAffiliation(node.data?.affliationUnit);
    if (clusterAffiliation) result.data = { affliationUnit: clusterAffiliation };
    return result;
  };

  // add any left over nodes
  for (const contextId in contexts) {
    const context = contexts[contextId];
    if (
      context !== undefined
      // && context?.value?.id === context?.value?.metroRegion
    ) {
      const nodeIds = context?.nodeIds;
      if (nodeIds) {
        for (let i = 0; i < nodeIds.length; i++) {
          const x = nodeIds[i];
          const node = nodes[x]?.node;
          if (node !== undefined && items[x] === undefined) {
            if (
              canAddNode(
                node,
                isRegionsFilterEmpty,
                isConstituenciesEmpty,
                constitencies,
                nodeFilters,
                hiddenNodes,
                affiliationClusterMode,
              )
            ) {
              if (!nodeFiltersEnabled) {
                items[x] = buildLeftoverNodeItem(node);
              } else if (nodeFilterType === HIGHLIGHTED_STATE) {
                if (!nodeTypeFilterOut.includes(node.data.nodeType)) {
                  items[x] = buildLeftoverNodeItem(node);
                }
              } else if (nodeFilterType === SELECTED_STATE) {
                if (
                  nodeOptions.includes(node.data.nodeType) &&
                  canAddNode(
                    node,
                    isRegionsFilterEmpty,
                    isConstituenciesEmpty,
                    constitencies,
                    nodeFilters,
                    hiddenNodes,
                    affiliationClusterMode,
                  )
                ) {
                  items[x] = buildLeftoverNodeItem(node);
                }
              }
            }
          }
        }
      }
    }
  }

  // add p2p nodes — use addNodeCtx since p2p applies selection styles
  for (let i = 0; i < p2pIds.length; i++) {
    const p2pContext = p2pIds[i];
    const p2pNodeIds = p2pContext?.nodeIds;
    if (p2pNodeIds) {
      for (let j = 0; j < p2pNodeIds.length; j++) {
        const nodeId = p2pNodeIds[j];
        if (items[nodeId] === undefined) {
          const node = nodes[nodeId]?.node;
          if (node) {
            addNodeCtx(node, items, nodeId, filterCtx);
          }
        }
      }
    }
  }

  perfMeasure('selector:items', 'selector:items:start');
  return items;
};

export const applySelectionStylesToItems = (
  items,
  nodes,
  nodeOptions,
  nodeFilterType,
  nodeTypeFilterOut,
  shortestPathNodeid = '',
) => {
  perfMark('selector:styled:start');
  perfCount('selector:styled:runs');
  if (!items) {
    perfMeasure('selector:styled', 'selector:styled:start');
    return items;
  }

  const nodeFiltersEnabled = nodeOptions.length > 0 || nodeTypeFilterOut.length > 0;
  const shouldStyleNode = (nodeType) => {
    if (!nodeFiltersEnabled) {
      return false;
    }
    if (nodeFilterType === HIGHLIGHTED_STATE) {
      return nodeOptions.includes(nodeType) && !nodeTypeFilterOut.includes(nodeType);
    }
    if (nodeFilterType === SELECTED_STATE) {
      return nodeOptions.includes(nodeType);
    }
    return !nodeTypeFilterOut.includes(nodeType);
  };

  let styledItems = items;
  const ensureClone = () => {
    if (styledItems === items) {
      styledItems = { ...items };
    }
  };

  for (const itemId in items) {
    const item = items[itemId];
    if (!item || item.id1 != null || item.id2 != null) {
      continue;
    }

    if (item.isContextNode) {
      continue;
    }

    const node = nodes[itemId]?.node;
    if (!node?.data) {
      continue;
    }

    if (shouldStyleNode(node.data.nodeType)) {
      const nodeSelectionStyles = GetNodeSelectionStyles(node);
      ensureClone();
      styledItems[itemId] = {
        ...item,
        ...nodeSelectionStyles,
      };
    }
  }

  if (shortestPathNodeid) {
    const pathNode = nodes[shortestPathNodeid]?.node;
    if (pathNode) {
      const pathNodeSelectionStyles = GetNodeSelectionStyles(pathNode);
      ensureClone();
      styledItems[shortestPathNodeid] = {
        ...pathNode,
        ...pathNodeSelectionStyles,
        glyphs: [
          {
            size: 1,
            label: {
              text: '-',
              color: '#fff',
            },
          },
        ],
      };
    }
  }

  perfMeasure('selector:styled', 'selector:styled:start');
  return styledItems;
};
